import get from 'lodash/get.js';
import has from 'lodash/has.js';
import head from 'lodash/head.js';
import merge from 'lodash/merge.js';
import unset from 'lodash/unset.js';

import AutomatedSurveyAssignment from '@/models/AutomatedSurveyAssignment.js';
import Remediation from '@/models/Remediation.js';
import axios from '@/plugins/axios.js';
import toastService from '@/services/ToastService.js';
import BaseQuiz from '@/store/modules/base/BaseQuiz.js';
import Quiz from '@/store/modules/quizattempt/Quiz.js';
import QuizAttemptHelper from '@/store/utilities/QuizAttemptHelper.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

// eslint-disable-next-line id-denylist
export default merge(BaseQuiz({ name: 'QuizAttempt', item: Quiz }), {
    namespaced: true,

    state: {
        api: {
            answers: '/api/quiz-attempt-answers',
            authorize_quiz: '/api/quiz-attempt-authorizations',
            questions: '/api/quiz-attempt-questions',
            quizzes: '/api/quizzes',
            quiz_attempt: '/api/quiz-attempts',
        },
    },

    getters: {
        api: state => route => state.api[route],

        all: state => () => {
            const items = { ...state };
            unset(items, 'api');
            unset(items, 'loading');
            unset(items, 'sort');
            unset(items, 'filter');
            unset(items, 'paginate');

            return items;
        },
    },

    actions: {
        launchQuiz: async (context, { payload, router = null, type = null, attemptId = null }) => {
            const helper = new QuizAttemptHelper({ store: context, router, payload, attemptId });

            const quizAttempt = await helper.authorizeQuizAttempt({ quizType: type });

            if (!quizAttempt) return null;

            // Check if a previous quiz attempt is hydrated.
            const hydratedQuiz = context.state[quizAttempt.quiz_version_id];

            if (hydratedQuiz.quiz_attempt?.id && hydratedQuiz.quiz_attempt.id !== quizAttempt.id) {
                // Reset the state of any previously hydrated quiz attempt that doesn't match the current one.
                // This prevents state conflicts when a user has multiple attempts on the same quiz,
                // which could cause data corruption and UI inconsistencies between attempts.
                await context.dispatch(`QuizAttempt/${hydratedQuiz.id}/cleanHydratedAttempt`, null, { root: true });
            }

            await context.dispatch(
                `QuizAttempt/${payload.id}/hydrateQuizAttempt`,
                { quiz_attempt: quizAttempt },
                { root: true }
            );

            if (router) helper.navigateToQuiz(type, payload.id, attemptId);

            return quizAttempt;
        },

        getAttempts: async ({ rootGetters, dispatch }, { quiz_activity_id: quizActivityId, type }) => {
            const userId = rootGetters['User/id'];

            let attempts = await dispatch('fetch', {
                id: null,
                query: `?filter[${type}_activity_id]=${quizActivityId}&per_page=100`,
                route: 'quiz_attempt',
                type: 'all',
            });

            attempts = attempts.filter(attempt => attempt.user_id === userId);

            return attempts;
        },

        hydrateQuiz: async (
            { rootGetters, state, dispatch, commit },
            { router, launch, attemptId, quizId, activityId }
        ) => {
            let activeAttempt;
            // If quiz is already hydrated do nothing

            if (has(state, quizId)) return;

            commit('TOGGLE_LOADING');

            const activity = await axios
                .get(`/api/activities/${activityId}?include=contentable`)
                .then(resp => resp.data.data);

            const quiz = await dispatch('fetch', {
                id: quizId,
                route: 'quizzes',
                query: '?include=questionsCount',
                type: 'single',
            });

            // get remediation filter by user, activity, course
            const userId = rootGetters['User/id'];

            if (!attemptId) {
                activeAttempt = head(
                    await dispatch('fetch', {
                        id: null,
                        query: `?filter[user_id]=${userId}&filter[active_attempt]=1`,
                        route: 'quiz_attempt',
                        type: 'all',
                    })
                );
            }

            const queryBuilder = new QueryBuilder('remediation');
            queryBuilder
                .filter('course_id', activity.course_id)
                .filter('activity_id', activity.id)
                .filter('user_id', userId);

            await Remediation.api().get(queryBuilder.toUrl());

            const remediation = Remediation.query()
                .where('course_id', activity.course_id)
                .where('activity_id', activity.activity_id)
                .where('user_id', userId)
                .first();

            if (remediation && Number(get(remediation, 'quiz_time_limit')) > 0) {
                activity.activity_content.time_limit = remediation.quiz_time_limit;
            }

            quiz.quiz_activity = activity.activity_content;

            dispatch('add', { ...quiz });

            if (launch) {
                await dispatch('launchQuiz', {
                    payload: {
                        activityId,
                        quizActivityId: activity.activity_content.id,
                        courseId: activity.course_id,
                        quizVersionId: quizId,
                        id: quizId,
                        lockdown: activity.activity_content.lockdown,
                    },
                    router,
                    attemptId: attemptId || activeAttempt?.id,
                });
            }

            commit('TOGGLE_LOADING');
        },

        hydrateAutomatedSurvey: async (
            { state, dispatch, commit },
            { router, launch, attemptId, surveyId, assignmentId }
        ) => {
            // If quiz is already hydrated do nothing
            if (has(state, surveyId)) return null;

            // commit('TOGGLE_LOADING');

            let survey;
            let assignment;

            const assignmentQuery = new QueryBuilder(`automated-survey-assignments/${assignmentId}`).include([
                'course',
                'activity',
                'automatedSurvey',
                'subject',
            ]);

            return Promise.all([
                dispatch('fetch', {
                    id: surveyId,
                    route: 'quizzes',
                    query: '?include=questionsCount',
                    type: 'single',
                }),
                axios.get(assignmentQuery.toUrl()),
            ])
                .then(async ([surveyResponse, assignmentResponse]) => {
                    survey = surveyResponse;
                    assignment = assignmentResponse.data.data;

                    // Check if assignment is completed
                    if (assignment.completed_at) {
                        throw new Error('This evaluation has already been completed.');
                    }

                    await AutomatedSurveyAssignment.insert({ data: assignment });

                    survey.quiz_activity = assignment;

                    await dispatch('add', { ...survey });

                    if (launch) {
                        await dispatch('launchQuiz', {
                            payload: {
                                quizVersionId: surveyId,
                                id: surveyId,
                                quizActivityId: assignment.id,
                                assignment,
                            },
                            type: 'automatedSurvey',
                            router,
                            attemptId,
                        });
                    }

                    commit('TOGGLE_LOADING');
                })
                .catch(error => {
                    // Handle completed assignment error and redirect
                    if (error.message === 'This evaluation has already been completed.') {
                        toastService.open({
                            message: error.message,
                            type: 'is-danger',
                            duration: 5000,
                        });

                        if (router) {
                            router.push({ name: 'home' });
                        }
                    }

                    throw error;
                });
        },
    },

    mutations: {},
});
